import { useQuery } from '@tanstack/react-query';
import { getSellerOrdersForProduct } from '@util/firestore/orders/getSellerOrdersForProduct';
import { formatTimestamp } from '@util/index';
import { useAuth } from 'context/AuthContext';
import type { ProductDocument } from 'models/product';
import Link from 'next/link';

export const useSellerProductOrders = (product?: ProductDocument) => {
  const { userDoc } = useAuth();
  const isSeller = !!userDoc?.uid && userDoc.uid === product?.seller_id;

  const { data: orders = [] } = useQuery({
    queryKey: ['seller-product-orders', product?.id, userDoc?.uid],
    queryFn: () => getSellerOrdersForProduct(product!.id),
    enabled: isSeller && !!product?.id,
  });

  return { isSeller, orders };
};

export const SellerProductOrderLinks = ({
  orders,
  tone,
}: {
  orders: ReturnType<typeof useSellerProductOrders>['orders'];
  tone: 'dark' | 'light';
}) => (
  <div className="flex max-h-[24rem] flex-col gap-[0.8rem] overflow-y-auto">
    {orders.map((order) => (
      <Link
        key={order.id}
        href={`/dashboard/orders/${order.id}`}
        className={`flex items-center justify-between gap-[1.6rem] rounded-2xl px-[1.6rem] py-[1.2rem] text-[1.4rem] transition-colors ${
          tone === 'dark'
            ? 'bg-white/10 text-brand-white hover:bg-white/20'
            : 'bg-[#F6F6F6] text-brand-black hover:bg-[#ececec]'
        }`}
      >
        <span className="font-semibold">Order #{order.order_num}</span>
        <span className={tone === 'dark' ? 'text-white/70' : 'text-[#7f7f7f]'}>
          {formatTimestamp(order.created, true)}
        </span>
        <span className="font-semibold underline">View details</span>
      </Link>
    ))}
  </div>
);

const SellerProductOrders = ({ product }: { product: ProductDocument }) => {
  const { isSeller, orders } = useSellerProductOrders(product);

  if (!isSeller || !orders.length) return null;

  return (
    <div className="flex w-full flex-col gap-[1.2rem] rounded-3xl border border-[#d9d9d9] bg-white p-[2.4rem]">
      <span className="text-[1.6rem] font-semibold text-brand-black">
        You sold this item
      </span>
      <SellerProductOrderLinks orders={orders} tone="light" />
    </div>
  );
};

export default SellerProductOrders;
